import { FINANCIAL_COMPARE_DECIMALS } from '@/const/Financial'

// Unknown currency stays distinct from a confirmed currency; no exchange rate is implied.
export const financialCommonCurrency = (currencies: string[]): string | null => {
  const unique = new Set(currencies)
  return unique.size > 1 ? null : (currencies[0] ?? '')
}

// XLSX writers store small doubles in exponent form (9.5000000000000001E-2). Expand them exactly.
const expandExponent = (text: string): string => {
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(text)
  if (!match || Math.abs(Number(match[4])) > 30) return text
  const [, sign, whole, fraction = '', exponent] = match
  let digits = whole + fraction
  let point = whole.length + Number(exponent)
  if (point <= 0) {
    digits = '0'.repeat(1 - point) + digits
    point = 1
  }
  digits = digits.padEnd(point, '0')
  const integer = digits.slice(0, point).replace(/^0+(?=\d)/, '')
  const decimals = digits.slice(point).replace(/0+$/, '')
  return `${sign}${integer}${decimals ? `.${decimals}` : ''}`
}

// Keep source decimals exact. Rounding belongs to display and comparison, never to imported values.
export const financialDecimal = (value: string): string | null => {
  const text = expandExponent(value.trim().replace(',', '.'))
  return /^-?\d{1,18}(?:\.\d{1,20})?$/.test(text) ? text : null
}

export const sumFinancialAmounts = (values: string[]): string => {
  const scale = values.reduce((maximum, value) => Math.max(maximum, value.split('.')[1]?.length ?? 0), 0)
  const total = values.reduce((sum, value) => {
    const negative = value.startsWith('-')
    const [whole, fraction = ''] = value.replace(/^-/, '').split('.')
    const units = BigInt(whole + fraction.padEnd(scale, '0'))
    return sum + (negative ? -units : units)
  }, 0n)
  const digits = (total < 0n ? -total : total).toString().padStart(scale + 1, '0')
  const result = scale ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}` : digits
  return `${total < 0n ? '-' : ''}${result}`
}

export const subtractFinancialAmounts = (left: string, right: string): string => {
  return sumFinancialAmounts([left, right.startsWith('-') ? right.slice(1) : `-${right}`])
}

// Half away from zero, exact on the decimal string.
export const roundFinancialAmount = (value: string, decimals = FINANCIAL_COMPARE_DECIMALS): string => {
  const negative = value.startsWith('-')
  const [whole, fraction = ''] = value.replace(/^-/, '').split('.')
  const kept = BigInt(whole + fraction.slice(0, decimals).padEnd(decimals, '0'))
  const rounded = Number(fraction[decimals] ?? '0') >= 5 ? kept + 1n : kept
  const digits = rounded.toString().padStart(decimals + 1, '0')
  const result = decimals ? `${digits.slice(0, -decimals)}.${digits.slice(-decimals)}` : digits
  return rounded === 0n ? result : `${negative ? '-' : ''}${result}`
}

export const financialAmountsEqual = (left: string, right: string, decimals = FINANCIAL_COMPARE_DECIMALS): boolean => {
  return Number(roundFinancialAmount(subtractFinancialAmounts(left, right), decimals)) === 0
}

export const financialAmountLabel = (value: string | null, currency = ''): string => {
  if (value === null) return '—'
  return `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(Number(value))}${currency ? ` ${currency}` : ''}`
}

// Differences show every decimal that the comparison uses, so a "different" result never reads as 0.00.
export const financialDifferenceLabel = (value: string, currency = ''): string => {
  const rounded = roundFinancialAmount(value)
  const text = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: FINANCIAL_COMPARE_DECIMALS,
  }).format(Number(rounded))
  return `${Number(rounded) > 0 ? '+' : ''}${text}${currency ? ` ${currency}` : ''}`
}

export const financialPercent = (part: string, whole: string): string | null => {
  if (!Number(whole)) return null
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format((Number(part) / Number(whole)) * 100)}%`
}

// Exact decimal multiplication, rounded half away from zero.
export const multiplyFinancialAmount = (value: string, factor: string, decimals = 2): string => {
  const scale = (text: string): [bigint, number] => {
    const [whole, fraction = ''] = text.replace(/^-/, '').split('.')
    return [BigInt(whole + fraction) * (text.startsWith('-') ? -1n : 1n), fraction.length]
  }
  const [left, leftScale] = scale(value)
  const [right, rightScale] = scale(factor)
  const product = left * right
  const drop = leftScale + rightScale - decimals
  const negative = product < 0n
  let units = negative ? -product : product
  if (drop > 0) {
    const divisor = 10n ** BigInt(drop)
    units = (units + divisor / 2n) / divisor
  } else units *= 10n ** BigInt(-drop)
  const digits = units.toString().padStart(decimals + 1, '0')
  const result = decimals ? `${digits.slice(0, -decimals)}.${digits.slice(-decimals)}` : digits
  return units === 0n ? result : `${negative ? '-' : ''}${result}`
}
